import { useState, useRef } from 'react'
import type { JSX } from 'react'
import { clsx } from 'clsx'
import { useQuery } from '@apollo/client/react'

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Button,
  ChannelAvatar,
  CheckIcon,
  CounterInput,
  Dialog,
  Flex,
  Heading,
  CircleHelpIcon,
  Link,
  Notice,
  Paragraph,
  PencilIcon,
  Popover,
  RadioCard,
  Skeleton,
  Text,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'
import type { ChannelType } from '@buffer-mono/popcorn'

import { useCrossAppNavigate } from '../../../../common/hooks/useCrossAppNavigate'
import { getChannelSettingsUrl } from '../../../../common/utils/urls'
import { useUser } from '../../../../common/context/User'
import type { Channel } from '../../../../common/types'
import { DEFAULT_POSTING_GOAL, POSTING_GOAL_OPTIONS } from './constants'
import { Confetti } from './Confetti'
import {
  useChannelConnectionTracking,
  trackPostingGoalSkipped,
} from './hooks/useChannelConnectionTracking'
import { usePostingGoalMutation } from './hooks/usePostingGoalMutation'
import { GET_POSTING_SCHEDULE_INFO } from './queries'
import { transformScheduleIntoPostingTimePreview } from './helpers/transformScheduleIntoPostingTimePreview'

import styles from './ChannelConnectionSuccess.module.css'

type Step = 'step1' | 'step2'

interface BaseStepProps {
  channels: Channel[]
  channelIds: string[]
  channel: Channel
  isMobile: boolean
}

interface Step1Props extends BaseStepProps {
  selectedOption: string
  selectedGoal: string
  loading: boolean
  showConfetti: boolean
  onSelectedOptionChange: (value: string) => void
  onSelectedGoalChange: (value: string) => void
  onProceedToStep2: () => Promise<void>
}

function Step1({
  channels,
  channelIds,
  channel,
  isMobile,
  selectedOption,
  selectedGoal,
  loading,
  showConfetti,
  onSelectedOptionChange,
  onSelectedGoalChange,
  onProceedToStep2,
}: Step1Props): JSX.Element {
  const [helpPopoverOpen, setHelpPopoverOpen] = useState(false)

  return (
    <>
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap="lg"
        className={styles.main}
      >
        <Dialog.Header className={styles.header}>
          {showConfetti && (
            <Confetti
              className={styles.confetti}
              duration={5000}
              startDelay={300}
            />
          )}
          <div className={styles.avatarWrapper}>
            {channelIds?.length > 1 ? (
              <ChannelAvatar.Stack
                stacking="firstOnTop"
                max={5}
                className={styles.avatarStack}
              >
                {channels.map((channel) => (
                  <ChannelAvatar
                    key={channel.id}
                    channel={channel.service as ChannelType}
                    src={channel.avatar}
                    alt={channel.name}
                    size="large"
                    className={styles.avatar}
                  />
                ))}
              </ChannelAvatar.Stack>
            ) : (
              <ChannelAvatar
                channel={channel.service as ChannelType}
                src={channel.avatar}
                alt={channel.name}
                size="large"
                className={styles.avatar}
              />
            )}
            <div className={styles.check}>
              <CheckIcon color="inverted" />
            </div>
          </div>
          <Dialog.Title size={isMobile ? 'md' : 'lg'} className={styles.title}>
            How many times a week would you like to post?
          </Dialog.Title>
          <Dialog.Description className={styles.description}>
            This posting goal will tell us how many times to recommend per week.
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.Body className={styles.body}>
          <RadioCard.Group
            value={selectedOption}
            onValueChange={(value: string): void => {
              if (value !== 'custom') {
                onSelectedGoalChange(value)
              }
              onSelectedOptionChange(value)
            }}
            className={styles.slimRadioCardGroup}
          >
            {POSTING_GOAL_OPTIONS.map((option) => (
              <RadioCard
                key={option.goal}
                value={option.goal.toString()}
                className={styles.slimRadioCard}
              >
                <Flex align="center" gap="md">
                  <div
                    className={clsx(
                      styles.goalFrequency,
                      styles[option.color as keyof typeof styles],
                    )}
                  >
                    {option.goal}x
                  </div>
                  <Text size={isMobile ? 'sm' : 'lg'} weight="medium">
                    {option.title}
                  </Text>
                </Flex>
              </RadioCard>
            ))}
            <RadioCard
              key="custom"
              value="custom"
              className={styles.slimRadioCard}
            >
              <Flex align="center" gap="md" className={styles.customGoal}>
                <div className={clsx(styles.goalFrequency, styles.pink)}>
                  <PencilIcon />
                </div>
                <Flex
                  align="center"
                  justify="between"
                  gap="md"
                  className={styles.customGoal}
                >
                  <Text size={isMobile ? 'sm' : 'lg'} weight="medium">
                    Choose your goal
                  </Text>
                  {selectedOption === 'custom' && (
                    <CounterInput
                      value={Number(selectedGoal)}
                      min={0}
                      max={100}
                      step={1}
                      onChange={(value: number): void =>
                        onSelectedGoalChange(value.toString())
                      }
                    />
                  )}
                </Flex>
              </Flex>
            </RadioCard>
          </RadioCard.Group>
        </Dialog.Body>
      </Flex>
      <Dialog.Footer showSeparator>
        <Flex align="center" justify="between" fullWidth>
          <Popover open={helpPopoverOpen} onOpenChange={setHelpPopoverOpen}>
            <Popover.Trigger>
              <Button
                variant="tertiary"
                size="large"
                className={styles.footerButtonAlignLeft}
              >
                <CircleHelpIcon color="subtle" />
                What&apos;s a Recommended Time?
              </Button>
            </Popover.Trigger>
            <Popover.Content align="start" side="top">
              <Flex
                direction="column"
                gap="md"
                className={styles.helpPopoverContent}
              >
                <Paragraph>
                  A recommended time is a posting time suggestion based on
                  Buffer data and industry trends, when your audience is most
                  likely to be active.
                </Paragraph>
                <Flex
                  align="center"
                  gap="sm"
                  justify="end"
                  className={styles.helpPopoverActions}
                >
                  <Button
                    size="small"
                    variant="tertiary"
                    onClick={() =>
                      window.open(
                        'https://support.buffer.com/articles/setting-posting-goals-in-buffer-Kcie9zPCy9',
                        '_blank',
                      )
                    }
                  >
                    Learn More
                  </Button>
                  <Button
                    size="small"
                    variant="primary"
                    onClick={() => setHelpPopoverOpen(false)}
                  >
                    Got It
                  </Button>
                </Flex>
              </Flex>
            </Popover.Content>
          </Popover>
          <Button
            size="large"
            variant="primary"
            loading={loading}
            disabled={!selectedGoal || loading}
            onClick={async () => {
              await onProceedToStep2()
            }}
          >
            Next <ArrowRightIcon />
          </Button>
        </Flex>
      </Dialog.Footer>
    </>
  )
}

interface Step2Props extends BaseStepProps {
  postingGoal: number
  loading: boolean
  error: unknown
  dismissModal: () => void
  onBackToStep1: () => void
  onCreatePostingPlan: () => void
}

function Step2({
  channels,
  channelIds,
  channel,
  isMobile,
  postingGoal,
  loading,
  error,
  dismissModal,
  onBackToStep1,
  onCreatePostingPlan,
}: Step2Props): JSX.Element {
  const navigateCrossApp = useCrossAppNavigate()

  // Fetch posting schedule data after mutation completes
  const { data: scheduleData, loading: scheduleLoading } = useQuery(
    GET_POSTING_SCHEDULE_INFO,
    {
      variables: { input: { id: channel.id } },
      fetchPolicy: 'cache-and-network',
    },
  )

  const postingTimes = transformScheduleIntoPostingTimePreview(
    scheduleData?.channel?.postingSchedule ?? [],
  )

  const isLoading = loading || scheduleLoading

  const handleCustomizeSchedule = (): void => {
    // Navigate to Posting Schedule Editor page for the channel
    dismissModal()
    navigateCrossApp(getChannelSettingsUrl({ channelId: channel.id }))
  }

  return (
    <>
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap="lg"
        className={styles.step2Main}
      >
        <Dialog.Header className={styles.header}>
          <div className={styles.avatarWrapper}>
            {channelIds?.length > 1 ? (
              <ChannelAvatar.Stack
                stacking="firstOnTop"
                max={5}
                className={styles.avatarStack}
              >
                {channels.map((channel) => (
                  <ChannelAvatar
                    key={channel.id}
                    channel={channel.service as ChannelType}
                    src={channel.avatar}
                    alt={channel.name}
                    size="large"
                    className={styles.avatar}
                  />
                ))}
              </ChannelAvatar.Stack>
            ) : (
              <ChannelAvatar
                channel={channel.service as ChannelType}
                src={channel.avatar}
                alt={channel.name}
                size="large"
                className={styles.avatar}
              />
            )}
          </div>
          <Dialog.Title size={isMobile ? 'md' : 'lg'} className={styles.title}>
            These are your recommended times to post
          </Dialog.Title>
          <Dialog.Description className={styles.description}>
            Customize them, block days and more from channel settings. Reach and
            engagement depend on more than timing.{' '}
            <Link
              href="https://buffer.com/resources/best-time-to-post-social-media/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more
            </Link>
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.Body className={styles.body}>
          {!!error && (
            <Notice variant="error" className={styles.error}>
              There was an error creating your posting goal. Please try again.
            </Notice>
          )}
          <div className={styles.schedulePreview}>
            {isLoading
              ? Array.from({ length: postingGoal }, (_, index) => (
                  <Skeleton
                    key={index}
                    width="100%"
                    height="49.5px"
                    className={styles.skeleton}
                  />
                ))
              : postingTimes.map((slot, index) => (
                  <div key={index} className={styles.schedulePreviewItem}>
                    <CheckIcon />{' '}
                    <Heading
                      size="small"
                      className={styles.schedulePreviewItemTitle}
                    >
                      {slot.day} from <strong>{slot.startTime}</strong> to{' '}
                      <strong>{slot.endTime}</strong>
                    </Heading>
                  </div>
                ))}
          </div>
        </Dialog.Body>
      </Flex>
      <Dialog.Footer showSeparator>
        <Flex align="center" justify="between" fullWidth>
          <Button
            variant="tertiary"
            size="large"
            onClick={onBackToStep1}
            className={styles.footerButtonAlignLeft}
          >
            <ArrowLeftIcon /> Change Goal
          </Button>
          <Flex align="center" gap="sm">
            <Button
              variant="tertiary"
              size="large"
              onClick={handleCustomizeSchedule}
            >
              Customize
            </Button>
            <Button
              variant="primary"
              size="large"
              onClick={onCreatePostingPlan}
            >
              Got It
            </Button>
          </Flex>
        </Flex>
      </Dialog.Footer>
    </>
  )
}

export function ChannelConnectionSuccess({
  channelIds: passedChannelIds,
  dismissModal,
}: {
  channelIds?: string[]
  dismissModal: () => void
}): JSX.Element {
  const account = useUser()
  const organization = account?.currentOrganization

  const [currentStep, setCurrentStep] = useState<Step>('step1')
  const hasVisitedStep2 = useRef(false)
  const [selectedOption, setSelectedOption] = useState<string>(
    DEFAULT_POSTING_GOAL.goal.toString(),
  )
  const [selectedGoal, setSelectedGoal] = useState<string>(
    DEFAULT_POSTING_GOAL.goal.toString(),
  )

  const { channels, channel, loading, error, createPostingGoal } =
    usePostingGoalMutation({ channelIds: passedChannelIds })

  useChannelConnectionTracking({
    organizationId: organization?.id,
    channel,
    commonTrackingProperties: organization?.commonTrackingProperties,
  })

  const isMobile = useIsMobileScreenSize()

  if (!channel) {
    return <></>
  }

  const channelIds = channels.map((c) => c.id)

  const handleProceedToStep2 = async (): Promise<void> => {
    // Trigger the mutation when advancing to step 2
    try {
      await createPostingGoal(Number(selectedGoal))
    } catch {
      // Error is handled by the hook and will be displayed in step 2
    }
    // Always proceed to step 2, even if there's an error
    setCurrentStep('step2')
    hasVisitedStep2.current = true
  }

  const handleBackToStep1 = (): void => {
    setCurrentStep('step1')
  }

  const handleCreatePostingPlan = (): void => {
    // Mutation already ran when advancing to step 2, just dismiss
    dismissModal()
  }

  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      trackPostingGoalSkipped({
        organizationId: organization?.id,
        channel,
        commonTrackingProperties: organization?.commonTrackingProperties,
      })
      dismissModal()
    }
  }

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <Dialog.Content size="xlarge" className={styles.content}>
        <Dialog.CloseButton />
        {currentStep === 'step1' ? (
          <Step1
            channels={channels}
            channelIds={channelIds}
            channel={channel}
            isMobile={isMobile}
            selectedOption={selectedOption}
            selectedGoal={selectedGoal}
            loading={loading}
            showConfetti={!hasVisitedStep2.current}
            onSelectedOptionChange={setSelectedOption}
            onSelectedGoalChange={setSelectedGoal}
            onProceedToStep2={handleProceedToStep2}
          />
        ) : (
          <Step2
            channels={channels}
            channelIds={channelIds}
            channel={channel}
            isMobile={isMobile}
            postingGoal={Number(selectedGoal)}
            loading={loading}
            error={error}
            dismissModal={dismissModal}
            onBackToStep1={handleBackToStep1}
            onCreatePostingPlan={handleCreatePostingPlan}
          />
        )}
      </Dialog.Content>
    </Dialog>
  )
}
